import React, { FC, useContext, useRef, useState } from 'react';

import {SerializedStyles} from '@emotion/react';
import { InputBase, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import {alpha, Theme} from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import { useHistory, useLocation } from 'react-router-dom';

import CloseIcon from '../../assets/icons/CloseIcon';
import SearchIcon from '../../assets/icons/SearchIcon';
import { UserInfoCtx } from '../../containers/CommonLayout/CommonLayout';
import styled, {css} from '../../styled';
import {colorsPalette, MIDDLE_MARGIN_PX, SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX} from '../../theme';
import { useEventListener } from '../../utils/hooks';
import useOverflowHidden from '../../utils/hooks/useOverflowHidden';
import useSearchClient from '../../utils/hooks/useSearchClient';
import Dropdown from './Dropdown';

interface SearchInputProps {
  wrapperCss?: SerializedStyles | (({ theme }: { theme: Theme }) => SerializedStyles);
  isOpen: boolean,
  toggle: () => void,
}

const SearchInput: FC<SearchInputProps> = ({ wrapperCss, isOpen = false, toggle }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [value, setValue] = useState('');
  const { userLoading } = useContext(UserInfoCtx);
  const selectedCategoriesRef = useRef([]);
  useOverflowHidden(isOpen);
  const { search: currentSearchParams } = useLocation();

  const { searchClient, indexName, loadingApiKey } = useSearchClient('autocomplete');

  const onChange = (e) => {
    setValue(e.target.value);
  };

  const search = () => {
    if (!isOpen) return;
    if (!value) return;
    toggle();

    const params = new URLSearchParams(currentSearchParams);
    params.delete('hierarchicalMenu[categories.lvl0]');
    params.set('s', value);
    selectedCategoriesRef.current?.forEach((p) =>
      params.append('hierarchicalMenu[categories.lvl0]', p),
    );

    history.push(`/discover/search/?${params.toString()}`);
  };

  useEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      search();
    }
  });

  useEventListener('keyup', (e) => {
    if (e.key === 'Escape' && isOpen) {
      toggle();
    }
  });

  const resetSearch = () => setValue('');

  const validValue = value?.length >= 2;

  return (
    (<Wrapper
      onClick={!isOpen ? toggle : () => {}}
      optionalStyles={wrapperCss}
      openedCss={isOpen ? openedCss : undefined}
    >
      {isOpen && (
        <CloseSearch
          show={isOpen && !value}
          data-testid="closeButton"
          aria-label="close"
          onClick={toggle}
          size="large"
        >
          <StyledCloseIcon />
        </CloseSearch>
      )}
      {isOpen && (
        <ResetSearch
          show={isOpen && value}
          data-testid="clearSearchBox"
          aria-label="clear"
          onClick={() => {
            resetSearch();
          }}
          size="large"
        >
          <StyledCloseIcon />
        </ResetSearch>
      )}
      <SearchWrapper>
        <InputWrapper >
          {isOpen && (
            <StyledInput
              isOpen={isOpen}
              value={value}
              onChange={onChange}
              placeholder={t('input.placeholder.search')}
              autoFocus={isOpen}
              inputProps={{ 'aria-label': 'enter key words', 'data-testid': 'searchField' }}
            />
          )}
          {!isOpen && (
            <Placeholder isOpen={isOpen} data-testid="placeholder">
              {t('input.placeholder.search')}
            </Placeholder>
          )}
          <StyledIconButton
            isOpen={isOpen}
            data-testid="submitSearch"
            type="submit"
            aria-label="search"
            onClick={isOpen ? search : toggle}
            size="large"
          >
            <StyledSearchIcon />
          </StyledIconButton>
        </InputWrapper>
        <InstantSearch searchClient={searchClient} indexName={indexName}>
          <Configure hitsPerPage={10} distinct={3} clickAnalytics />
          {isOpen && validValue && (
            <Dropdown
              selectedCategoriesRef={selectedCategoriesRef}
              value={value}
              close={toggle}
              canCheckAlgoliaToken={Boolean(
                !loadingApiKey && process.env.REACT_APP_ALGOLIA_APP_NAME && !userLoading,
              )}
            />
          )}
        </InstantSearch>
      </SearchWrapper>
    </Wrapper>)
  );
};

export default SearchInput;

const StyledInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: relative;
  top: -2px;
  padding-left: ${({ value }) => value && SUBMIDDLE_MARGIN_PX};
  flex-grow: 1;
  transition: 0.3s padding-left;
  font-size: 20px;

  & input::placeholder {
    font-size: 20px;
    color: ${alpha(colorsPalette.white, 0.33)};
  }
`;
const SearchWrapper = styled('div')`
  width: 100%;
`;
const ResetSearch = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  left: 4px;
  top: 50%;
  z-index: 1;
  transform: translateY(-50%) scale(${({ show }) => (show ? 1 : 0.8)});
  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.3s;
  transition-delay: ${({ show }) => (show ? '0.2s' : 0)};
`;
const CloseSearch = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'show',
})`
  position: absolute;
  left: ${({ show }) => (show ? '-50px' : '-40px')};
  top: 50%;
  transform: translateY(-50%) scale(${({ show }) => (show ? 1 : 0.8)});

  visibility: ${({ show }) => (show ? 'visible' : 'hidden')};
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.3s;
  transition-delay: ${({ show }) => (show ? '0.2s' : 0)};
`;
const StyledCloseIcon = styled(CloseIcon)`
  min-width: 18px;
  width: 18px;
  height: 18px;
`;
const Placeholder = styled(Typography)`
  white-space: nowrap;
  position: relative;
  top: -2px;
  flex-grow: 1;
  margin-right: ${SMALL_MARGIN_PX};
  font-size: 20px;
  display: none;
  color: ${alpha(colorsPalette.white, 0.33)};

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: ${({ isOpen }) => !isOpen && 'block'};
  }

  ${({ theme }) => theme.breakpoints.up('xl') + 'and (max-width: 1536px)'} {
    display: none;
  }
`;
const StyledIconButton = styled(IconButton)`
  ${({ theme }) => theme.breakpoints.up('xl') + 'and (max-width: 1536px)'} {
    padding: ${({ isOpen }) => !isOpen && '15px'};
  }
`;

const Wrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles' && prop !== 'openedCss',
})`
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    border-radius: 999em;
    background: ${alpha(colorsPalette.white, 0.33)};
    height: 54px;
    padding-left: 25px;
    padding-right: 4px;
    position: relative;
    flex-basis: 260px;
    margin-left: ${MIDDLE_MARGIN_PX};
  }
  ${({ theme }) => theme.breakpoints.up('xl') + 'and (max-width: 1536px)'} {
    flex-basis: 54px;
    padding: 0;
  }
  && {
    ${({ openedCss }) => openedCss}
  }
  ${({ optionalStyles }) => optionalStyles}
`;
const openedCss = ({ theme }) => css`
  position: relative;
  background: ${alpha(colorsPalette.white, 0.33)};
  border-radius: 999em;
  height: 54px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 30px;
  padding-right: 4px;
  ${theme.breakpoints.up('sm')} {
    max-width: 480px;
    flex-grow: 1;
    min-width: 300px;
  }
`;
const InputWrapper = styled('div')`
	position: relative;
	width: 100%;
	display: flex;
	align-items: center;
`;
const StyledSearchIcon = styled(SearchIcon)`
  width: 24px;
  height: 24px;
`;
